import React, { useEffect, useState } from "react";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import DeviceAIChat from "./DeviceAIChat";
import { formatDateTime } from "../utils/datetime";
import AIActionReview from "./AIActionReview";

type AIFlags = {
  ai_enabled: boolean;
  ai_ticket_enabled: boolean;
  ai_action_enabled: boolean;
  ai_alert_enabled: boolean;
};

let cachedFlags: AIFlags | null = null;

export function useAIFlags() {
  const [flags, setFlags] = useState<AIFlags | null>(cachedFlags);
  useEffect(() => {
    if (cachedFlags) return;
    api
      .get("/api/v1/ai/flags")
      .then((r) => {
        cachedFlags = r.data;
        setFlags(r.data);
      })
      .catch(() => {});
  }, []);
  return flags;
}

type DeviceRecAction = {
  type: string;
  params: Record<string, any>;
  reason?: string;
  action_id?: number;
  risk_tier?: "low" | "medium" | "high";
  status?: string;
};
type DeviceRec = {
  severity: "info" | "warning" | "critical";
  title: string;
  detail: string;
  action?: DeviceRecAction;
};

const DESTRUCTIVE_ACTIONS = new Set([
  "reboot_device",
  "shutdown_device",
  "install_patch",
]);

/** Alert diagnosis, rendered as prose.
 *
 * alert-assist returns an OBJECT — {summary, likely_cause, suggested_action,
 * plan} — and the panel used to fall through to JSON.stringify for anything that
 * was not a string. A technician mid-incident was shown a JSON blob.
 */
function AlertDiagnosis({ data }: { data: Record<string, any> }) {
  const plan: string[] = Array.isArray(data.plan) ? data.plan.filter(Boolean) : [];
  return (
    <div className="space-y-1.5">
      {data.summary && (
        <div className="text-sm text-gray-900 dark:text-gray-100">{data.summary}</div>
      )}
      {data.likely_cause && (
        <div>
          <strong>Likely cause:</strong> {data.likely_cause}
        </div>
      )}
      {data.suggested_action && (
        <div>
          <strong>Best next action:</strong> {data.suggested_action}
        </div>
      )}
      {plan.length > 0 && (
        <div>
          {/* The point of a plan over a single suggestion: when the first move
              does not fix it, the technician still has somewhere to go. */}
          <strong>If that does not fix it:</strong>
          <ol className="list-decimal list-inside mt-0.5 space-y-0.5">
            {plan.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>
      )}
      {data.plan_caveat && (
        <div className="text-amber-700 dark:text-amber-400">{data.plan_caveat}</div>
      )}
      <div className="text-[11px] text-gray-500 dark:text-gray-400 pt-0.5">
        AI-generated from this alert's monitoring data — verify before acting on a
        production system.
      </div>
    </div>
  );
}

/** Ticket classification, rendered as prose rather than a JSON dump. */
function TicketClassification({ data }: { data: Record<string, any> }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 flex-wrap">
        <strong>Suggested:</strong>
        {data.priority && (
          <span className="rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5">
            {String(data.priority)}
          </span>
        )}
        {data.category && (
          <span className="rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5">
            {String(data.category)}
          </span>
        )}
      </div>
      {data.rationale && (
        <div className="text-gray-600 dark:text-gray-300">{String(data.rationale)}</div>
      )}
      {/* Suggested, never applied: priority moves SLA clocks. */}
      <div className="text-[11px] text-gray-500 dark:text-gray-400">
        Suggestion only — priority drives SLA timers, so nothing changes until you
        set it.
      </div>
    </div>
  );
}

/** Reusable AI assist panel. Pass exactly one of {ticketId, alertId, deviceId}. */
export default function AIAssistPanel({
  ticketId,
  autoRun = false,
  alertId,
  deviceId,
  onActioned,
  onUseDraft,
}: {
  ticketId?: number;
  /** Run immediately on mount. For callers whose own button already reads
   *  "Diagnose" — otherwise the user clicks twice to ask one question. */
  autoRun?: boolean;
  alertId?: number;
  deviceId?: number;
  /** Fired after a proposal is approved and run, so the page underneath can
   *  reload — a comment posted from here should appear on the ticket. */
  onActioned?: () => void;
  /** Hand a drafted reply to the page's own reply box so the tech can edit it
   *  before sending. Absent on screens with nowhere to put it. */
  onUseDraft?: (text: string) => void;
}) {
  const flags = useAIFlags();
  const [busy, setBusy] = useState(false);
  // Auto-run guard. A ref rather than state because it must not itself trigger a
  // render, and because two renders in the same tick would otherwise both see
  // `false` and fire two paid AI calls.
  const autoRan = React.useRef(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  // Review and run a proposal HERE. Sending somebody to the AI Actions queue
  // to approve the reply the AI just drafted on this ticket is the same
  // complaint as on Alerts: you are already looking at the thing.
  const [reviewing, setReviewing] = useState<number | null>(null);
  const [tone, setTone] = useState("friendly");
  const [instruction, setInstruction] = useState(
    "Diagnose this device's current state and propose any actions that would help."
  );
  const [recs, setRecs] = useState<DeviceRec[] | null>(null);
  const [recsGeneratedAt, setRecsGeneratedAt] = useState<string | null>(null);
  const [refreshingRecs, setRefreshingRecs] = useState(false);
  // Running a command on a machine is an admin action, and the server enforces
  // that too — this only decides whether to offer the button.
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  React.useEffect(() => {
    // Fire once when a caller whose own control already says "Diagnose" mounts
    // this panel, so asking one question costs one click instead of two. Still
    // user-initiated, so the AI spend is intentional.
    if (!autoRun || autoRan.current || !alertId) return;
    if (!flags?.ai_enabled || !flags?.ai_alert_enabled) return;
    autoRan.current = true;
    setBusy(true);
    api
      .post("/api/v1/ai/alert-assist", { alert_id: alertId })
      .then((r) => setResult(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e?.message))
      .finally(() => setBusy(false));
  }, [autoRun, alertId, flags?.ai_enabled, flags?.ai_alert_enabled]);

  // Load stored recommendations for this device (proactive advisor output)
  useEffect(() => {
    if (!deviceId || !flags?.ai_enabled) return;
    api
      .get(`/api/v1/ai/device-advisor/${deviceId}/recommendations`)
      .then((r) => {
        setRecs(r.data.recommendations || []);
        setRecsGeneratedAt(r.data.generated_at);
      })
      .catch(() => {});
  }, [deviceId, flags?.ai_enabled]);

  const refreshRecs = async () => {
    if (!deviceId) return;
    setRefreshingRecs(true);
    try {
      await api.post(`/api/v1/ai/device-advisor/run/${deviceId}`);
      const r = await api.get(`/api/v1/ai/device-advisor/${deviceId}/recommendations`);
      setRecs(r.data.recommendations || []);
      setRecsGeneratedAt(r.data.generated_at);
    } catch (e: any) {
      alert("Advisor run failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setRefreshingRecs(false);
    }
  };

  if (!flags?.ai_enabled) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded p-3 text-sm text-gray-600 dark:text-gray-400">
        AI mode is off. Enable it in <a className="text-blue-600 dark:text-blue-400 underline" href="/ai-settings">AI Settings</a>.
      </div>
    );
  }

  const callTicket = async (op: string) => {
    if (!ticketId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post("/api/v1/ai/ticket-assist", {
        ticket_id: ticketId,
        operation: op,
        tone,
      });
      setResult(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message);
    } finally {
      setBusy(false);
    }
  };

  const callAlert = async () => {
    if (!alertId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post("/api/v1/ai/alert-assist", { alert_id: alertId });
      setResult(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message);
    } finally {
      setBusy(false);
    }
  };

  const callDeviceDiagnose = async () => {
    if (!deviceId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post("/api/v1/ai/device-diagnose", {
        device_id: deviceId,
        instruction,
      });
      setResult(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-violet-50 to-blue-50 dark:from-violet-950/40 dark:to-blue-950/30 border border-violet-200 dark:border-violet-800/60 rounded p-3 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">
          ⚡ AI Assist
        </span>
        {busy && <span className="text-xs text-gray-500 dark:text-gray-400">thinking…</span>}
      </div>

      {ticketId && flags.ai_ticket_enabled && (
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => callTicket("summarize")}
            disabled={busy}
            className="px-3 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded text-xs font-medium disabled:opacity-50"
          >
            Summarize
          </button>
          <button
            onClick={() => callTicket("classify")}
            disabled={busy}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium disabled:opacity-50"
          >
            Classify
          </button>
          <div className="flex items-center gap-1 ml-2">
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="text-xs border rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            >
              <option value="friendly">friendly</option>
              <option value="neutral">neutral</option>
              <option value="formal">formal</option>
              <option value="apologetic">apologetic</option>
            </select>
            <button
              onClick={() => callTicket("suggest_reply")}
              disabled={busy}
              className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium disabled:opacity-50"
            >
              Suggest reply
            </button>
          </div>
        </div>
      )}

      {ticketId && !flags.ai_ticket_enabled && (
        <div className="text-xs text-gray-500 dark:text-gray-400">Ticket AI is disabled in AI Settings.</div>
      )}

      {alertId && flags.ai_alert_enabled && (
        <button
          onClick={callAlert}
          disabled={busy}
          className="px-3 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded text-xs font-medium disabled:opacity-50"
        >
          Diagnose + suggest a plan
        </button>
      )}

      {/* The conversation, above the one-shot outputs. Recommendations answer
          "what should I look at"; the chat answers "and then what?" — which is
          the question a technician actually has, and the one the device page
          could not answer at all before this. */}
      {deviceId && <DeviceAIChat deviceId={deviceId} canRun={isAdmin} />}

      {deviceId && (
        <div className="space-y-2 bg-white dark:bg-gray-800 border border-violet-200 dark:border-violet-800/60 rounded p-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wide">
              AI Recommendations
            </div>
            <button
              onClick={refreshRecs}
              disabled={refreshingRecs}
              className="text-xs text-violet-600 dark:text-violet-300 hover:underline disabled:opacity-50"
              title="Run the advisor against this device now"
            >
              {refreshingRecs ? "running…" : "↻ run now"}
            </button>
          </div>
          {recsGeneratedAt && (
            <div className="text-[10px] text-gray-500 dark:text-gray-400">
              generated {formatDateTime(recsGeneratedAt)}
            </div>
          )}
          {!recs || recs.length === 0 ? (
            <div className="text-xs text-gray-500 dark:text-gray-400 italic">
              No recommendations yet. The daily advisor runs at 04:00 ET, or click "run now".
            </div>
          ) : (
            <ul className="space-y-1.5">
              {recs.map((r, i) => (
                <li
                  key={i}
                  className={`text-xs px-2 py-1.5 rounded border-l-4 ${
                    r.severity === "critical"
                      ? "bg-red-50 dark:bg-red-900/30 border-red-500 text-red-900 dark:text-red-200"
                      : r.severity === "warning"
                        ? "bg-amber-50 dark:bg-amber-900/30 border-amber-500 text-amber-900 dark:text-amber-200"
                        : "bg-blue-50 dark:bg-blue-900/30 border-blue-400 text-blue-900 dark:text-blue-200"
                  }`}
                >
                  <div className="font-semibold">{r.title}</div>
                  <div className="opacity-90">{r.detail}</div>
                  {r.action && deviceId && (
                    <RecommendationAction
                      deviceId={deviceId}
                      action={r.action}
                      onChange={(updated) =>
                        setRecs((prev) =>
                          (prev || []).map((x, idx) =>
                            idx === i ? { ...x, action: updated } : x
                          )
                        )
                      }
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {deviceId && flags.ai_action_enabled && (
        <div className="space-y-2">
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            className="w-full text-xs border rounded p-2 h-16 bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            placeholder="What should the AI investigate or do?"
          />
          <button
            onClick={callDeviceDiagnose}
            disabled={busy}
            className="px-3 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded text-xs font-medium disabled:opacity-50"
          >
            Diagnose + propose
          </button>
        </div>
      )}

      {error && (
        <div className="text-xs bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 px-2 py-1 rounded">{error}</div>
      )}

      {result && (
        <div className="text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 space-y-1 text-gray-900 dark:text-gray-100">
          {result.summary && (
            <div>
              {typeof result.summary === "string" ? (
                <>
                  <strong>Summary:</strong> {result.summary}
                </>
              ) : (
                <AlertDiagnosis data={result.summary} />
              )}
            </div>
          )}
          {result.reply && (
            <div className="whitespace-pre-wrap">
              <strong>Suggested reply:</strong>
              <div className="mt-1 p-2 bg-gray-50 dark:bg-gray-900/50 rounded">{result.reply}</div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {/* Edit-first is the common case, so it leads. */}
                {onUseDraft && result.reply && (
                  <button
                    type="button"
                    onClick={() => onUseDraft(result.reply)}
                    className="text-xs px-3 py-1.5 rounded-lg bg-brand text-white font-medium"
                  >
                    Use as my reply
                  </button>
                )}
                {result.action_id && (
                  <button
                    type="button"
                    onClick={() => setReviewing(result.action_id)}
                    className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
                      onUseDraft
                        ? "border border-brand/40 text-brand dark:text-brand-light hover:bg-brand/5"
                        : "bg-brand text-white"
                    }`}
                  >
                    Send as written
                  </button>
                )}
                <span className="text-[11px] text-gray-400 dark:text-gray-500">
                  Nothing reaches the customer until you send it.
                </span>
              </div>
            </div>
          )}
          {result.classification && typeof result.classification === "object" && (
            <TicketClassification data={result.classification} />
          )}
          {result.classification && typeof result.classification !== "object" && (
            <div>
              <strong>Classification:</strong>
              <pre className="text-xs bg-gray-50 dark:bg-gray-900/50 rounded p-1 mt-1">
                {JSON.stringify(result.classification, null, 2)}
              </pre>
            </div>
          )}
          {result.text && !result.reply && (
            <div className="whitespace-pre-wrap">{result.text}</div>
          )}
          {result.proposed_action_ids && result.proposed_action_ids.length > 0 && (
            <div className="text-xs flex items-center gap-2 flex-wrap">
              <span className="text-gray-500 dark:text-gray-400">Proposed:</span>
              {result.proposed_action_ids.map((id: number) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setReviewing(id)}
                  className="px-2 py-1 rounded-md border border-brand/40 text-brand dark:text-brand-light hover:bg-brand/5"
                >
                  Review #{id}
                </button>
              ))}
            </div>
          )}
          {result.action_id && !result.reply && (
            <button
              type="button"
              onClick={() => setReviewing(result.action_id)}
              className="text-xs px-3 py-1.5 rounded-lg bg-brand text-white font-medium"
            >
              Review &amp; run
            </button>
          )}
        </div>
      )}

      {reviewing !== null && (
        <AIActionReview
          actionId={reviewing}
          onClose={() => setReviewing(null)}
          onDone={onActioned}
        />
      )}
    </div>
  );
}

function RecommendationAction({
  deviceId,
  action,
  onChange,
}: {
  deviceId: number;
  action: DeviceRecAction;
  onChange: (updated: DeviceRecAction) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const status = action.status || "proposed";
  if (!action.action_id) return null;

  const needsSchedule = DESTRUCTIVE_ACTIONS.has(action.type);

  const doExecute = async (delaySeconds: number | null, note: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(
        `/api/v1/ai/actions/${action.action_id}/approve-and-execute`,
        { note: note || undefined, delay_seconds: delaySeconds ?? undefined }
      );
      onChange({ ...action, status: r.data.status });
      setScheduleOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const clickRun = async () => {
    if (needsSchedule) {
      setScheduleOpen(true);
      return;
    }
    let note = "";
    if (action.risk_tier === "high") {
      const entered = window.prompt(
        `HIGH-risk action — enter approval note (required):\nAction: ${action.type}\nParams: ${JSON.stringify(action.params)}`,
        ""
      );
      if (!entered) return;
      note = entered;
    } else {
      if (
        !window.confirm(
          `Approve and execute on this device?\n\nAction: ${action.type}\nParams: ${JSON.stringify(action.params)}`
        )
      )
        return;
    }
    await doExecute(null, note);
  };

  const reject = async () => {
    if (!window.confirm("Reject this AI-proposed action?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/api/v1/ai/actions/${action.action_id}/reject`, {});
      onChange({ ...action, status: r.data.status });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message);
    } finally {
      setBusy(false);
    }
  };

  if (status === "succeeded")
    return <div className="mt-1.5 text-[10px] text-green-700 dark:text-green-400 font-mono">✓ executed ({action.type})</div>;
  if (status === "failed")
    return <div className="mt-1.5 text-[10px] text-red-700 dark:text-red-400 font-mono">✗ execution failed ({action.type})</div>;
  if (status === "rejected")
    return <div className="mt-1.5 text-[10px] text-gray-500 dark:text-gray-400 font-mono italic">rejected</div>;

  return (
    <>
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        <span
          className={`text-[9px] uppercase font-bold px-1 py-0.5 rounded ${
            action.risk_tier === "high"
              ? "bg-red-200 dark:bg-red-900/50 text-red-900 dark:text-red-200"
              : action.risk_tier === "medium"
                ? "bg-amber-200 dark:bg-amber-900/50 text-amber-900 dark:text-amber-200"
                : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          }`}
        >
          {action.risk_tier} risk
        </span>
        <code className="text-[10px] bg-white/60 dark:bg-gray-900/60 dark:text-gray-100 px-1 rounded">{action.type}</code>
        <button
          onClick={clickRun}
          disabled={busy}
          className="text-[10px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold disabled:opacity-50"
        >
          {busy ? "running…" : needsSchedule ? "▶ Approve & Schedule" : "▶ Approve & Execute"}
        </button>
        <button
          onClick={reject}
          disabled={busy}
          className="text-[10px] px-2 py-0.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700/40 rounded"
        >
          Reject
        </button>
      </div>
      {error && <div className="text-[10px] text-red-700 dark:text-red-400 mt-1">{error}</div>}
      {scheduleOpen && (
        <RebootScheduleModal
          deviceId={deviceId}
          actionType={action.type}
          requiresNote={action.risk_tier === "high"}
          onClose={() => setScheduleOpen(false)}
          onSubmit={(delay, note) => doExecute(delay, note)}
          busy={busy}
        />
      )}
    </>
  );
}

function RebootScheduleModal({
  deviceId,
  actionType,
  requiresNote,
  onClose,
  onSubmit,
  busy,
}: {
  deviceId: number;
  actionType: string;
  requiresNote: boolean;
  onClose: () => void;
  onSubmit: (delaySeconds: number, note: string) => void;
  busy: boolean;
}) {
  const [kind, setKind] = useState<"now" | "maintenance" | "custom">("maintenance");
  const [customAt, setCustomAt] = useState<string>(""); // datetime-local value
  const [note, setNote] = useState("");
  const [mw, setMw] = useState<{
    available: boolean;
    next_start_at: string | null;
    delay_seconds: number | null;
    label: string | null;
  } | null>(null);
  const [loadingMw, setLoadingMw] = useState(true);

  useEffect(() => {
    setLoadingMw(true);
    api
      .get(`/api/v1/devices/${deviceId}/next-maintenance-window`)
      .then((r) => setMw(r.data))
      .catch(() => setMw({ available: false, next_start_at: null, delay_seconds: null, label: null }))
      .finally(() => setLoadingMw(false));
  }, [deviceId]);

  const submit = () => {
    if (requiresNote && !note.trim()) {
      alert("Approval note required for HIGH-risk actions.");
      return;
    }
    let delay = 60;
    if (kind === "now") {
      delay = 60;
    } else if (kind === "maintenance") {
      if (!mw?.available || !mw.delay_seconds) {
        alert(
          "No maintenance window configured for this customer. Pick 'Custom time' or 'Reboot now' instead."
        );
        return;
      }
      delay = mw.delay_seconds;
    } else if (kind === "custom") {
      if (!customAt) {
        alert("Pick a date/time.");
        return;
      }
      // NOT utcMillis: customAt is a <input type="datetime-local"> value, which
      // is LOCAL time the user typed — not a zone-less UTC timestamp from the
      // API. Parsing it as UTC would shift the scheduled time by the browser's
      // offset, which is the same bug this sweep exists to remove, inverted.
      const target = new Date(customAt).getTime();
      const now = Date.now();
      const sec = Math.max(60, Math.floor((target - now) / 1000));
      if (sec > 60 * 60 * 24 * 30) {
        if (!window.confirm(`That's ${Math.floor(sec / 86400)} days out. Continue?`)) return;
      }
      delay = sec;
    }
    onSubmit(delay, note);
  };

  const verb = actionType === "shutdown_device" ? "shutdown" : actionType === "install_patch" ? "patch install" : "reboot";
  const Verb = verb.charAt(0).toUpperCase() + verb.slice(1);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-5 w-full max-w-md space-y-3">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Schedule {verb}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          This will run a {verb} command on the device. Pick when you want it to fire.
        </p>

        <div className="space-y-2">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="kind"
              checked={kind === "now"}
              onChange={() => setKind("now")}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">{Verb} now</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">~60 seconds from now (shutdown.exe /t 60)</div>
            </div>
          </label>

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="kind"
              checked={kind === "maintenance"}
              onChange={() => setKind("maintenance")}
              className="mt-1"
              disabled={!mw?.available}
            />
            <div>
              <div className={`font-medium text-gray-900 dark:text-gray-100 ${!mw?.available ? "text-gray-400 dark:text-gray-500" : ""}`}>
                Next maintenance window
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {loadingMw
                  ? "checking…"
                  : mw?.available
                    ? `${mw.label} — fires at ${formatDateTime(mw.next_start_at!)}`
                    : "no maintenance window configured for this customer"}
              </div>
            </div>
          </label>

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="kind"
              checked={kind === "custom"}
              onChange={() => setKind("custom")}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-gray-100">At a specific time</div>
              <input
                type="datetime-local"
                value={customAt}
                onChange={(e) => setCustomAt(e.target.value)}
                onClick={() => setKind("custom")}
                className="mt-1 border rounded px-2 py-1 text-sm w-full dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
          </label>
        </div>

        {requiresNote && (
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
              Approval note (required for HIGH risk)
            </label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              placeholder="e.g. customer approved overnight reboot for patch install"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/40"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-medium disabled:opacity-50"
          >
            {busy ? "scheduling…" : `Approve & Schedule ${verb}`}
          </button>
        </div>
      </div>
    </div>
  );
}
